import { PageController } from '@client/components/_core/layout/PageController';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { User } from '@client/components/_ui/display/User';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import {
    PaymentListAction,
    type PaymentListRow,
    PaymentListStatusCell,
    usePaymentListStatuses,
} from '@client/components/claims/financials/payment-approval/payment-list-status';
import { usePaymentApprovalEnabled } from '@client/components/claims/financials/payment-approval/use-payment-approval-enabled';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpcOptions } from '@client/trpc/client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type MouseEvent, useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/compensation-payments',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Linked payments',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CompensationPayments />
        </PageController>
    );
}

type QueryParams = RouterInputs['compensationPayment']['listCompensationPayments'];
type PaymentItem = RouterOutputs['compensationPayment']['listCompensationPayments']['rows'][number];

const columnHelper = createColumnHelper<PaymentItem, QueryParams['orderBy']>();

function CompensationPayments() {
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const { reserveId } = Route.useParams();
    const { formatDate, formatCurrency } = useFormatter();
    const navigate = useNavigate();
    const paymentApproval = usePaymentApprovalEnabled();

    const [input, setInput] = useState<QueryParams>({
        orderBy: { id: 'desc' },
    });

    const { data, isLoading, error } = useQuery(
        trpcOptions.compensationPayment.listCompensationPayments.queryOptions({
            ...input,
            where: {
                ...input.where,
                reserveId,
                Compensation: {
                    // Under payment approval (CUR-2672, PAY-L1), every raised payment's items, each with
                    // where its payment is; a draft is not a payment yet.
                    status: paymentApproval ? { not: 'DRAFT' } : 'INITIALIZED',
                },
            },
        }),
    );
    // Off, no ids, so nothing is read.
    const paymentStatuses = usePaymentListStatuses(
        id,
        paymentApproval ? (data?.rows ?? []).map(({ Compensation }) => Compensation.id) : [],
    );

    // The item's details, as the tab has always opened them.
    const openItem = (item: PaymentItem, event?: MouseEvent) =>
        navigate(
            {
                to: '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/financials/compensation-payments/details/$compensationPaymentId',
                params: {
                    orgId,
                    id,
                    compensationId: item.Compensation.id,
                    compensationPaymentId: item.id,
                },
            },
            event,
        );
    // Under payment approval, a raised payment opens its review (CUR-2673), as on the claim's payments
    // list; an item of a payment counted before payment approval has none, so its details open.
    const openPayment = (item: PaymentItem, { isRaised }: PaymentListRow, event: MouseEvent) =>
        isRaised
            ? navigate(
                  {
                      to: '/console/$orgId/claims/details/$id/financials/payments/$compensationId',
                      params: { orgId, id, compensationId: item.Compensation.id },
                  },
                  event,
              )
            : openItem(item, event);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        // A descriptive key: the claim page's own table may hold `q` underneath this overlay (CUR-1341).
        urlKey: 'payments',
        queryError: error,
        emptyState: { noRecordsTitle: 'No compensation payments linked to this reserve' },
        pagination: {
            position: 'bottom',
        },
        columns: [
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Date created',
                sortable: 'createdAt',
                cell: ({ row }) => formatDate(row.createdAt, 'dd MMM yyyy'),
            }),
            columnHelper.accessor((row) => row.CreatedBy?.firstName, {
                id: 'createdBy',
                header: 'Created by',
                sortable: 'CreatedBy.firstName',
                cell: ({ row }) => (
                    <User
                        userId={row.CreatedBy?.userId}
                        photoUrl={row.CreatedBy?.photoUrl}
                        name={row.CreatedBy?.fullName}
                    />
                ),
            }),
            columnHelper.accessor((row) => row.Compensation?.TransactionType?.name, {
                id: 'transactionType',
                header: 'Transaction type',
                sortable: 'Compensation.TransactionType.name',
                cell: ({ row }) => row.Compensation?.TransactionType?.name,
            }),
            columnHelper.accessor((row) => row.Compensation?.costCodeFullName, {
                id: 'costCode',
                header: 'Cost code',
                cell: ({ row }) => row.Compensation?.costCodeFullName,
            }),
            columnHelper.accessor('description', {
                id: 'description',
                header: 'Description',
                cell: ({ row }) => <ComponentPreview>{row.description}</ComponentPreview>,
                // Under payment approval, narrower, so Status and Review or View fit beside it.
                className: paymentApproval ? 'max-w-40' : 'max-w-xs',
            }),
            columnHelper.accessor('transactionAmount', {
                id: 'transactionAmount',
                header: 'Transaction amount',
                cell: ({ row }) => formatCurrency(row.transactionAmount),
            }),
            ...(paymentApproval
                ? [
                      columnHelper.display({
                          id: 'status',
                          header: 'Status',
                          // Wide enough for the tag and who it waits on, which the actions column would otherwise cover.
                          className: 'min-w-44',
                          cell: ({ row }) => (
                              <PaymentListStatusCell
                                  payment={paymentStatuses.byId.get(row.Compensation.id)}
                                  loading={paymentStatuses.isLoading}
                              />
                          ),
                      }),
                      columnHelper.actions({
                          id: 'open',
                          align: 'right',
                          cell: ({ row }) => {
                              const payment = paymentStatuses.byId.get(row.Compensation.id);
                              return payment === undefined ? null : (
                                  <PaymentListAction
                                      payment={payment}
                                      onOpen={(event) => openPayment(row, payment, event)}
                                  />
                              );
                          },
                      }),
                  ]
                : []),
        ],
    });

    return (
        <>
            {paymentStatuses.error != null && (
                <InlineQueryError
                    className="mb-4"
                    error={paymentStatuses.error}
                    onRetry={() => void paymentStatuses.refetch()}
                    retrying={paymentStatuses.isFetching}
                    title="Couldn't load where each payment is"
                    description="The payments show without their status until it loads — try again to load it."
                    compact
                />
            )}
            <PrismaTable
                {...tableProps}
                className="[&_.ant-table-cell]:align-top"
                onRow={(item) => {
                    if (!paymentApproval) {
                        return {
                            className: 'cursor-pointer',
                            onClick() {
                                openItem(item);
                            },
                        };
                    }
                    // Until its payment's status is read, a row cannot tell whether it opens a review or details.
                    const payment = paymentStatuses.byId.get(item.Compensation.id);
                    return payment === undefined
                        ? {}
                        : {
                              className: 'cursor-pointer',
                              onClick(event) {
                                  openPayment(item, payment, event);
                              },
                          };
                }}
            />
        </>
    );
}
